import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Save, Trash2, Loader2 } from "lucide-react";

export default function Settings({ project, reloadProject }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: project.title || "",
    purpose: project.purpose || "",
    tone: project.tone || "",
    audience: project.audience || "",
    terminology: project.terminology || "",
    default_mode: project.default_mode || "double_espresso",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${project.id}`, form);
      await reloadProject();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success("Project deleted");
      navigate("/dashboard");
    } catch {
      toast.error("Could not delete project");
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="animate-fade-in max-w-2xl space-y-8">
      <div>
        <h2 className="font-serif text-2xl font-medium tracking-tight">Project settings</h2>
        <p className="mt-2 text-muted-foreground">
          Project memory keeps every document consistent in tone, purpose and terminology.
        </p>
      </div>

      <div className="space-y-5 rounded-sm border border-border bg-card p-6">
        <div className="space-y-2">
          <Label>Title</Label>
          <Input value={form.title} onChange={set("title")} className="rounded-sm" data-testid="settings-title" />
        </div>
        <div className="space-y-2">
          <Label>Purpose</Label>
          <Textarea value={form.purpose} onChange={set("purpose")} className="rounded-sm" data-testid="settings-purpose" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Tone</Label>
            <Input value={form.tone} onChange={set("tone")} className="rounded-sm" data-testid="settings-tone" />
          </div>
          <div className="space-y-2">
            <Label>Audience</Label>
            <Input value={form.audience} onChange={set("audience")} className="rounded-sm" data-testid="settings-audience" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Terminology &amp; writing rules</Label>
          <Textarea
            value={form.terminology}
            onChange={set("terminology")}
            placeholder="Preferred terms, style rules, things to avoid…"
            className="rounded-sm"
            data-testid="settings-terminology"
          />
        </div>
        <div className="space-y-2">
          <Label>Default generation mode</Label>
          <Select value={form.default_mode} onValueChange={(v) => setForm({ ...form, default_mode: v })}>
            <SelectTrigger className="rounded-sm" data-testid="settings-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="espresso">Espresso — fast &amp; economical</SelectItem>
              <SelectItem value="double_espresso">Double Espresso — balanced</SelectItem>
              <SelectItem value="ristretto">Ristretto — max reasoning</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-sm" data-testid="settings-save">
          {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
          Save settings
        </Button>
      </div>

      <div className="rounded-sm border border-destructive/40 bg-destructive/5 p-6">
        <h3 className="font-serif text-lg font-semibold text-destructive">Danger zone</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Deleting a project permanently removes its structure and all documents.
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="mt-4 rounded-sm" data-testid="delete-project-button">
              <Trash2 size={16} className="mr-2" /> Delete project
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete “{project.title}”?</AlertDialogTitle>
              <AlertDialogDescription>
                This cannot be undone. All modules, sections and documents will be permanently removed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={del} className="rounded-sm bg-destructive text-destructive-foreground" data-testid="confirm-delete-project">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
